<template>
  <div class="font-sans bg-gray-50 text-gray-800">
    <!-- Header -->
    <header id="header" class="bg-white border-b border-gray-200 fixed w-full z-10">
      <div class="flex items-center px-4 py-3">
        <div class="flex items-center">
          <router-link to="/" class="text-xl font-bold flex items-center text-custom-button-blue hover:text-custom-dark-blue">
            <img
              src="@/assets/images/logos/sidebarlogo.png"
              alt="ClickAI"
              class="h-10"
            />
          </router-link>
        </div>
      </div>
    </header>

    <div id="content-wrapper" class="flex pt-14">
      <!-- Sidebar Navigation -->
      <div id="sidebar" class="fixed top-14 left-0 w-64 h-screen bg-white overflow-y-auto border-r border-gray-200">
        <div class="px-4 py-4">
          <ul class="space-y-1">
            <li>
              <span class="block py-2 px-3 bg-primary-50 text-primary-700 rounded-md font-medium cursor-pointer">
                <i class="fa-solid fa-book-open mr-2"></i> Introduction
              </span>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Leads')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage potential customers and track their interactions"
              >
                <span><i class="fa-solid fa-user-plus mr-2"></i> Leads</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Leads') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Leads')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/leads" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-custom-fields" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-new-lead" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-edit-lead" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit lead
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get lead by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-get-lead-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get leads by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/leads-add-call-log" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Deals')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Track sales opportunities and manage deal pipeline"
              >
                <span><i class="fa-solid fa-handshake mr-2"></i> Deals</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Deals') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Deals')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/deals" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-custom-fields" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-new-deal" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-edit-deal" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit deal
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deal by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/deals-get-deal-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get deals by phone
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Customers')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage customer profiles and relationships"
              >
                <span><i class="fa-solid fa-users mr-2"></i> Customers</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Customers') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Customers')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/customers" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-custom-fields" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get custom fields
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-create-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customer by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-get-customer-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get customers by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-edit-customer" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit customer
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/customers-add-call-log" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add call log
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Products')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage product catalog and variations"
              >
                <span><i class="fa-solid fa-box mr-2"></i> Products</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Products') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Products')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/products" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-all" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get all products
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-get-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get product by ID
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-create-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-edit-product" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit product
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-add-variations" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Add variations
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/products-delete-variation" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete variation
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Calendar')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Schedule meetings and manage appointments"
              >
                <span><i class="fa-solid fa-calendar mr-2"></i> Calendar</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Calendar') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Calendar')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/calendar" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-types" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get appointment types
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-appointment-users" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment users
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-appointment-type-details" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get appointment details
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-working-hours" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user working hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-break-hours" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get break hours
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-free-time-slot" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get free time slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-double-free-time-slot" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get double free slots
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-get-user-appointment-by-date" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Get user appointments
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-schedule-meeting" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Schedule meeting
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-create-event-type" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create event type
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/calendar-edit-event-type" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit event type
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Team')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage team members and user accounts"
              >
                <span><i class="fa-solid fa-users-gear mr-2"></i> Team</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Team') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Team')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/team" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/team-list" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-green-100 text-green-800 px-1 rounded mr-2">GET</span>
                    Get team members
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Tasks')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Create and manage tasks and assignments"
              >
                <span><i class="fa-solid fa-list-check mr-2"></i> Tasks</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Tasks') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Tasks')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/tasks" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-create-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-edit-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Edit task
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/tasks-delete-task" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-red-100 text-red-800 px-1 rounded mr-2">DEL</span>
                    Delete task
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Contacts')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Manage contact information and address book"
              >
                <span><i class="fa-solid fa-address-book mr-2"></i> Contacts</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Contacts') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Contacts')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/contacts" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-create-contact" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Create new contact
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-phone" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by phone
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/contacts-find-by-name" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Find by name
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="py-1">
              <div 
                @click="toggleSection('Chats')"
                class="flex items-center justify-between text-gray-700 hover:text-gray-900 py-2 px-3 rounded-md cursor-pointer hover:bg-gray-50"
                title="Send messages and manage chat communications"
              >
                <span><i class="fa-solid fa-comments mr-2"></i> Chats</span>
                <i 
                  class="fa-solid text-xs text-gray-400 transition-transform duration-200"
                  :class="openSections.includes('Chats') ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
              <ul v-if="openSections.includes('Chats')" class="mt-1 ml-6 space-y-1 border-l border-gray-200 pl-3">
                <li>
                  <router-link to="/api-overview/chats" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer font-semibold">
                    <i class="fa-solid fa-eye mr-2 text-primary-600"></i>
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link to="/endpoint/chats-send-message" class="block py-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                    <span class="text-xs bg-blue-100 text-blue-800 px-1 rounded mr-2">POST</span>
                    Send message
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      <!-- Main Content -->
      <div id="main-content" class="ml-64 flex-1 px-8 py-6 w-full">
        <div class="w-full">
          <div class="mb-8">
            <h1 class="text-3xl font-bold text-gray-800 mb-4">
              Click AI API Overview
            </h1>
            <p class="text-gray-600 text-lg mb-6">
              Automate your business workflows and integrate Click AI's powerful features into your applications with our comprehensive API.
            </p>
            <div class="flex flex-wrap gap-4 mb-6">
              <router-link to="/api-overview/leads" class="inline-flex items-center px-4 py-2 bg-custom-button-blue text-white rounded-md font-medium hover:bg-custom-button-blue-dark transition cursor-pointer">
                <i class="fa-solid fa-play mr-2"></i> Quick Start
              </router-link>
              <router-link to="/settings/api-settings" class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition cursor-pointer">
                                <i class="fa-solid fa-key mr-2"></i> Get API Keys
              </router-link>
            </div>
          </div>

          <div id="capabilities" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              API Capabilities
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-user-plus"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Lead Management</h3>
                </div>
                <p class="text-gray-600">Create and manage leads, track interactions, search by phone, and add call logs. 6 endpoints available.</p>
              </div>
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-handshake"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Deal Management</h3>
                </div>
                <p class="text-gray-600">Track deals through your pipeline, associate with customers and products. 5 endpoints available.</p>
              </div>
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-users"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Customer Management</h3>
                </div>
                <p class="text-gray-600">Maintain customer profiles, track interactions, and manage customer relationships. 6 endpoints available.</p>
              </div>
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-box"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Product Management</h3>
                </div>
                <p class="text-gray-600">Create products with variations, organize by categories, and manage inventory. 6 endpoints available.</p>
              </div>
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-calendar"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Calendar & Appointments</h3>
                </div>
                <p class="text-gray-600">Schedule meetings, find time slots, manage appointment types and working hours. 11 endpoints available.</p>
              </div>
              <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex items-center mb-3">
                  <div class="bg-custom-blue p-2 rounded-md text-custom-button-blue mr-3">
                    <i class="fa-solid fa-list-check"></i>
                  </div>
                  <h3 class="text-lg font-semibold">Tasks & Contacts</h3>
                </div>
                <p class="text-gray-600">Create and assign tasks, track progress, manage contacts, and send messages. 7 endpoints available.</p>
              </div>
            </div>
          </div>

          <div id="code-examples" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Code Examples
            </h2>
            <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
              <div class="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 class="font-semibold text-custom-dark-gray">Create New Lead Example</h3>
                                  <div class="relative">
                    <select 
                      v-model="selectedLanguage" 
                      class="pl-3 pr-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="javascript">JavaScript</option>
                      <option value="python">Python</option>
                      <option value="curl">cURL</option>
                      <option value="php">PHP</option>
                      <option value="ruby">Ruby</option>
                    </select>
                  </div>
              </div>
              <div class="bg-black text-white p-4 font-mono text-sm overflow-x-auto">
                <pre v-if="selectedLanguage === 'javascript'" class="language-javascript">const apiKey = 'your_api_key_here';
const url = 'http://localhost:5000/leads/new-lead';

const headers = {
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
};

// Example: Create a new lead
const leadData = {
    name: 'John Doe',
    phone: '050-1234567',
    status: 'new',
    source: 'website',
    hasCustomFields: false,
    email: 'yossi@example.com',
    company: 'חברת יוסי בע"מ'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(leadData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Lead created successfully:', data.lead_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));</pre>
                <pre v-if="selectedLanguage === 'python'" class="language-python">import requests
import json

api_key = "your_api_key_here"
url = "http://localhost:5000/leads/new-lead"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Example: Create a new lead
lead_data = {
    "name": "John Doe",
    "phone": "050-1234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": False,
    "email": "yossi@example.com",
    "company": "חברת יוסי בע\"מ"
}

response = requests.post(url, headers=headers, json=lead_data)

if response.status_code == 201:
    data = response.json()
    print("Lead created successfully:", data['lead_id'])
else:
    print(f"Error {response.status_code}: {response.json()}")</pre>
                <pre v-if="selectedLanguage === 'curl'" class="language-bash"># Create a new lead
curl -X POST "http://localhost:5000/leads/new-lead" \
  -H "Authorization: Bearer your_api_key_here" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "John Doe",
    "phone": "050-1234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": false,
    "email": "yossi@example.com",
    "company": "חברת יוסי בע\"מ"
  }'</pre>
                <pre v-if="selectedLanguage === 'php'" class="language-php">&lt;?php
$api_key = 'your_api_key_here';
$url = 'http://localhost:5000/leads/new-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Example: Create a new lead
$lead_data = [
    'name' => 'John Doe',
    'phone' => '050-1234567',
    'status' => 'new',
    'source' => 'website',
    'hasCustomFields' => false,
    'email' => 'yossi@example.com',
    'company' => 'חברת יוסי בע"מ'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Lead created successfully: " . $data['lead_id'];
} else {
    echo "Error $http_code: $response";
}
?></pre>
                <pre v-if="selectedLanguage === 'ruby'" class="language-ruby">require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('http://localhost:5000/leads/new-lead')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Example: Create a new lead
lead_data = {
  name: 'John Doe',
  phone: '050-1234567',
  status: 'new',
  source: 'website',
  hasCustomFields: false,
  email: 'yossi@example.com',
  company: 'חברת יוסי בע"מ'
}

request.body = lead_data.to_json
response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Lead created successfully: #{data['lead_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end</pre>
              </div>
              <div class="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button 
                  @click="copyCodeToClipboard"
                  class="px-3 py-1 bg-custom-button-blue text-white rounded text-sm font-medium hover:bg-custom-button-blue-dark transition"
                  :class="{ 'bg-green-600 hover:bg-green-700': copied }"
                >
                  <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'" class="mr-1"></i> 
                  {{ copied ? 'Copied!' : 'Copy Code' }}
                </button>
              </div>
            </div>
          </div>

          <div id="authentication" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Authentication
            </h2>
            <div class="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
              <p class="mb-4 text-gray-700">
                All API requests require authentication using API keys. You can generate API keys from your Click AI dashboard.
              </p>
              <div class="bg-yellow-100 border-l-4 border-yellow-400 p-4 mb-4">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <i class="fa-solid fa-triangle-exclamation text-yellow-400"></i>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-yellow-700">
                      Keep your API keys secure and never expose them.
                    </p>
                  </div>
                </div>
              </div>
              <h3 class="text-lg font-semibold mb-2">API Key Authentication</h3>
                            <div class="bg-gray-100 p-4 rounded-md font-mono text-sm mb-4">
                <code>Authorization: Bearer YOUR_API_KEY</code>
              </div>
            </div>
          </div>

          <div id="rate-limits" class="mb-10">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              Rate Limits
            </h2>
            <div class="overflow-hidden bg-white shadow-sm border border-gray-200 rounded-lg">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requests per minute
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Daily limit
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Free
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      10
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      50
                    </td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Pro
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      60
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      20,000
                    </td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Enterprise
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      300
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Unlimited
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div id="get-started" class="mb-10">
            <div class="bg-custom-blue border border-primary-200 rounded-lg p-6">
              <h2 class="text-2xl font-bold text-custom-dark-blue mb-3">
                Ready to get started?
              </h2>
              <p class="text-custom-medium-blue mb-4">
                Explore our API endpoints and integrate Click AI's powerful features into your applications.
              </p>
              <div class="flex flex-wrap gap-3">
                <router-link to="/endpoint/leads-custom-fields" class="inline-flex items-center px-4 py-2 bg-custom-button-blue text-white rounded-md font-medium hover:bg-custom-button-blue-dark transition cursor-pointer">
                  <i class="fa-solid fa-code mr-2"></i> Explore Endpoints
                </router-link>
                <router-link to="/api-overview/leads" class="inline-flex items-center px-4 py-2 bg-white border border-primary-300 text-custom-medium-blue rounded-md font-medium hover:bg-primary-50 transition cursor-pointer">
                  <i class="fa-solid fa-book mr-2 text-custom-medium-blue"></i> Read Documentation
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const openSections = ref(['Leads'])
const selectedLanguage = ref('javascript')
const copied = ref(false)

// Toggle section open/close - only one section can be open at a time
const toggleSection = (sectionName: string) => {
  const index = openSections.value.indexOf(sectionName)
  if (index > -1) {
    // אם התפריט כבר פתוח, סוגרים אותו
    openSections.value = []
  } else {
    // אם התפריט סגור, סוגרים את כל האחרים ופותחים רק אותו
    openSections.value = [sectionName]
  }
}

const getCodeExample = (language: string) => {
  const codeExamples: { [key: string]: string } = {
    javascript: `const apiKey = 'your_api_key_here';
const url = 'http://localhost:5000/leads/new-lead';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

// Example: Create a new lead
const leadData = {
    name: 'John Doe',
    phone: '050-1234567',
    status: 'new',
    source: 'website',
    hasCustomFields: false,
    email: 'yossi@example.com',
    company: 'חברת יוסי בע"מ'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(leadData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Lead created successfully:', data.lead_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
    
    python: `import requests
import json

api_key = "your_api_key_here"
url = "http://localhost:5000/leads/new-lead"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Example: Create a new lead
lead_data = {
    "name": "John Doe",
    "phone": "050-1234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": False,
    "email": "yossi@example.com",
    "company": "חברת יוסי בע\\"מ"
}

response = requests.post(url, headers=headers, json=lead_data)

if response.status_code == 201:
    data = response.json()
    print("Lead created successfully:", data['lead_id'])
else:
    print(f"Error {response.status_code}: {response.json()}")`,
    
    curl: `# Create a new lead
curl -X POST "http://localhost:5000/leads/new-lead" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "phone": "050-1234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": false,
    "email": "yossi@example.com",
    "company": "חברת יוסי בע\\"מ"
  }'`,
    
    php: `<?php
$api_key = 'your_api_key_here';
$url = 'http://localhost:5000/leads/new-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Example: Create a new lead
$lead_data = [
    'name' => 'John Doe',
    'phone' => '050-1234567',
    'status' => 'new',
    'source' => 'website',
    'hasCustomFields' => false,
    'email' => 'yossi@example.com',
    'company' => 'חברת יוסי בע"מ'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Lead created successfully: " . $data['lead_id'];
} else {
    echo "Error $http_code: $response";
}
?>`,
    
    ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('http://localhost:5000/leads/new-lead')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Example: Create a new lead
lead_data = {
  name: 'John Doe',
  phone: '050-1234567',
  status: 'new',
  source: 'website',
  hasCustomFields: false,
  email: 'yossi@example.com',
  company: 'חברת יוסי בע"מ'
}

request.body = lead_data.to_json
response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Lead created successfully: #{data['lead_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end`
  }
  
  return codeExamples[language] || codeExamples.javascript
}

const copyCodeToClipboard = async () => {
  try {
    const codeText = getCodeExample(selectedLanguage.value)
    
    // Use the Clipboard API if available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(codeText)
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea')
      textArea.value = codeText
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      document.execCommand('copy')
      textArea.remove()
    }
    
    // Show success feedback
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000) // Reset after 2 seconds
    
  } catch (err) {
    console.error('Failed to copy code: ', err)
    // Could add error notification here
  }
}
</script>

<style scoped>
/* Hide scrollbars */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Font Awesome icon styling */
.fa, .fas, .far, .fal, .fab {
  font-family: "Font Awesome 6 Free", "Font Awesome 6 Brands" !important;
}

/* Additional utility classes */
.font-mono {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.transition {
  transition: all 0.15s ease-in-out;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* HTTP method badges */
.bg-green-100 { background-color: #dcfce7; }
.text-green-800 { color: #166534; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
.bg-red-100 { background-color: #fee2e2; }
.text-red-800 { color: #991b1b; }

/* Category icon colors */
.text-blue-600 { color: #2563eb; }
.text-green-600 { color: #059669; }
.text-purple-600 { color: #9333ea; }
.text-orange-600 { color: #ea580c; }
.text-indigo-600 { color: #4f46e5; }
.text-red-600 { color: #dc2626; }
.text-yellow-600 { color: #d97706; }
.text-pink-600 { color: #db2777; }
.text-cyan-600 { color: #0891b2; }
</style> 